// Palette presets — 10 named candidates the workspace surfaces in its
// dropdown. Each preset is a list of CSS custom-property names; the runtime
// resolver (`resolveBrandPalette`) reads the live values from `:root` so
// any token edit propagates platform-wide without a rebuild.
//
// The set is a deliberate mix of:
//   - pure brand blue at three intensities (the safest, most on-brand)
//   - single-outcome-hue ramps (sky / indigo / violet / cyan — cool blue
//     family, all share the brand identity feel)
//   - multi-hue mixes that gradient between brand and an outcome hue
//
// All presets are designed to read on both light and dark page chrome
// when paired with `transparentBg: true`. Hex fallbacks are kept only for
// the default preset (`brand`) so SSR / DEFAULT_STATE always has a real
// palette; non-default presets resolve at runtime in the client.

import { resolvePaletteFromTokens } from '../core/browser/token-resolver';
import type { Palette } from '../types';

// 10 brand-only presets. Every entry pulls from `--brand-{50..950}` and
// every entry maintains at least a 5-stop luminance range from a darker
// shadow stop to a brighter highlight stop — that range is what preserves
// image-overlay fidelity (the mosaic dither needs varied luminance to map
// face shadows / mid-tones / highlights to distinct palette indices).
//
// Preset names suggest mood / weight, not function. Pick whichever feels
// right; once locked in, the other 9 can be dropped (the UI dropdown
// will collapse to a single hardcoded default).
// Lightness range targets: each preset spans ~0.55–0.70 OKLCh L from
// dimmest to brightest stop. Anything narrower and the dithered image
// reads as washed-out (insufficient contrast between adjacent quantised
// levels). Outcome ramps achieve ~0.58 L range with their default
// [900, 700, 500, 300, 200, 100] step list — these brand presets match
// or exceed that, so the depth feels comparable.
const PRESET_TOKENS_RAW = {
  // Balanced — full vertical range, even lightness spacing. Default.
  // L: 0.32 → 0.90 (range 0.58)
  brand: ['--brand-900', '--brand-600', '--brand-400', '--brand-200', '--brand-100'],
  // Maximum depth — runs from near-black navy to near-white pale.
  // L: 0.20 → 0.96 (range 0.76, the widest possible)
  deep: ['--brand-950', '--brand-700', '--brand-500', '--brand-300', '--brand-100'],
  // Saturated dark + wide range — keeps brand identity at the dim end.
  // L: 0.34 → 0.90 (range 0.56)
  rich: ['--brand-800', '--brand-700', '--brand-500', '--brand-300', '--brand-100'],
  // Light bias, 6 stops for fine tonal granularity.
  // L: 0.45 → 0.96 (range 0.51)
  bright: ['--brand-600', '--brand-500', '--brand-300', '--brand-200', '--brand-100', '--brand-50'],
  // Dark dramatic — heavy shadow grounding, contrasty.
  // L: 0.20 → 0.81 (range 0.61)
  shadow: ['--brand-950', '--brand-800', '--brand-600', '--brand-400', '--brand-200'],
  // Pale ethereal — mid down to white, 6 stops.
  // L: 0.45 → 0.96 (range 0.51)
  soft: ['--brand-600', '--brand-400', '--brand-300', '--brand-200', '--brand-100', '--brand-50'],
  // Saturation-peak heavy — vivid mid-band, classic brand feel.
  // L: 0.34 → 0.81 (range 0.47)
  bold: ['--brand-800', '--brand-600', '--brand-500', '--brand-400', '--brand-200'],
  // Extreme spread — big jumps for highest dither contrast.
  // L: 0.20 → 0.96 (range 0.76, biggest jumps between stops)
  wide: ['--brand-950', '--brand-700', '--brand-400', '--brand-200', '--brand-50'],
  // Even-spaced 5-stop — balanced classic dither feel.
  // L: 0.34 → 0.90 (range 0.56)
  classic: ['--brand-800', '--brand-600', '--brand-400', '--brand-200', '--brand-100'],
  // Glow — bright luminous endpoint.
  // L: 0.42 → 0.96 (range 0.54)
  glow: ['--brand-700', '--brand-500', '--brand-300', '--brand-100', '--brand-50'],
} as const;

const PRESET_TOKENS: Readonly<Record<keyof typeof PRESET_TOKENS_RAW, readonly string[]>> =
  PRESET_TOKENS_RAW;

export type PaletteName = keyof typeof PRESET_TOKENS_RAW;

export const PALETTE_NAMES: readonly PaletteName[] = Object.keys(PRESET_TOKENS) as PaletteName[];

export const DEFAULT_PALETTE_NAME: PaletteName = 'brand';

/**
 * Recommended palette names for dual-mode rendering. Currently both point
 * at the default preset — a brand-aligned set with `transparentBg: true`
 * reads on both light and dark page chrome with no per-mode bake.
 *
 * Engineers wanting genuinely distinct dark vs light variants (e.g. two
 * IPFS uploads per market cover) should change these to two different
 * preset names — every consumer that imports them picks up the new pair
 * platform-wide.
 */
export const RECOMMENDED_DARK_PALETTE_NAME: PaletteName = 'brand';
export const RECOMMENDED_LIGHT_PALETTE_NAME: PaletteName = 'brand';

// Hex fallback for the default preset only — used by `DEFAULT_STATE` and
// any caller that needs a palette without DOM access (SSR, unit tests).
// Lifted from the `/* #hex */` comments in globals.css:36–46.
const BRAND_DEFAULT_FALLBACK: Palette = ['#0000E8', '#2058EC', '#487AEC', '#7A9FED', '#ABC3F1'];

export const DEFAULT_PALETTE: Palette = BRAND_DEFAULT_FALLBACK;

/**
 * Records keyed by preset name with each entry's hex fallback. Only the
 * `brand` preset has a real hex array — others fall back to the same
 * brand default if runtime resolution fails. The workspace renders
 * client-only so this is rarely hit; SSR consumers should use the
 * default preset or pre-resolve.
 */
export const PALETTES: Readonly<Record<PaletteName, Palette>> = Object.fromEntries(
  PALETTE_NAMES.map((name): [PaletteName, Palette] => [
    name,
    name === 'brand' ? BRAND_DEFAULT_FALLBACK : BRAND_DEFAULT_FALLBACK,
  ]),
) as Record<PaletteName, Palette>;

/**
 * Get the CSS custom-property names that compose a preset. Pure — no DOM
 * access — so it's safe at module load. Useful for consumers wanting to
 * inspect or override the token list before resolving.
 */
export function brandRampTokens(name: PaletteName): readonly string[] {
  return PRESET_TOKENS[name];
}

/**
 * Resolve a preset name to a live `Palette` by reading the underlying CSS
 * variables from `:root`. Falls back to the brand-default hex array when
 * the document is unavailable (SSR) or any token fails to resolve.
 */
export function resolveBrandPalette(name: PaletteName): Palette {
  const tokens = PRESET_TOKENS[name];
  const fromTokens = resolvePaletteFromTokens(tokens);
  return fromTokens.length === tokens.length ? fromTokens : BRAND_DEFAULT_FALLBACK;
}
